import { Controller } from "@hotwired/stimulus";
import $ from "jquery";

/**
 * ラジオボタンの切り替えに応じてパネルを表示・非表示する
 * @example
 * <div
 *   data-controller="radio-toggle"
 *   data-radio-toggle-initial-panel-id-value="panel1">
 *  <input
 *    type="radio"
 *    data-action="click->radio-toggle#show"
 *    data-radio-toggle-panel-id-param="panel1">パネル1を表示
 *  <p
 *    data-radio-toggle-target="panel"
 *    data-panel-id="panel1">パネル1</p>
 * </div>
 */
export class RadioToggleController extends Controller {
  static targets = ["panel"];
  static values = {
    initialPanelId: String,
    disableInputs: { type: Boolean, default: false },
    panelId: { type: String, default: null }, // 内部で利用する
  };

  /** this.elementがビューポート内にあるか？ */
  #isIntersecting = false;
  #intersectionObserver = null;

  connect() {
    this.panelIdValue ??= this.initialPanelIdValue;
    this.#addIntersectionObserver();

    this.#update();
  }

  disconnect() {
    this.#intersectionObserver?.disconnect();
  }

  show(event) {
    const {
      params: { panelId },
    } = event;
    this.panelIdValue = panelId || event.currentTarget.value;
    this.#update();
  }

  #update() {
    for (const panel of this.panelTargets) {
      const isVisible = panel.dataset.panelId === this.panelIdValue;
      if (isVisible) {
        this.#showPanel(panel);
      } else {
        // 閉じる要素が画面上端を超えた場合はスクロールする
        const closedPanel = panel.parentElement;
        const rect = closedPanel.getBoundingClientRect();
        if (rect.top < 0) {
          closedPanel.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        this.#hidePanel(panel);
      }
      // disable inputs
      if (this.disableInputsValue) {
        for (const input of panel.querySelectorAll("input, select, textarea")) {
          // 初期状態を保存する
          if (input.dataset.defaultDisabled == null) {
            input.dataset.defaultDisabled = input.disabled;
          }
          if (isVisible) {
            input.disabled = getOriginalInput(input).dataset.defaultDisabled === "true";
          } else {
            input.disabled = true;
          }
        }
      }
    }
  }

  /** ビューポート内にある場合はアニメーション、そうでない場合は即座に表示する */
  #showPanel(panel) {
    $(panel).slideDown(this.#isIntersecting ? undefined : 0);
  }

  /** ビューポート内にある場合はアニメーション、そうでない場合は即座に非表示にする */
  #hidePanel(panel) {
    $(panel).slideUp(this.#isIntersecting ? undefined : 0);
  }

  /** this.elementがビューポート内にあるかどうかを設定する */
  #addIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    };
    const observer = new IntersectionObserver((entries) => {
      this.#isIntersecting = entries.some((entry) => entry.isIntersecting);
    }, options);
    this.#intersectionObserver = observer;

    observer.observe(this.element);
  }
}

/**
 * flatpickrはモバイルではinputを複製するため、元のinputを取得する。
 * flatpickrでない場合はそのまま返す。
 */
function getOriginalInput(input) {
  if (input.classList.contains("flatpickr-mobile")) {
    const original = input.previousElementSibling;
    if (original && original.classList.contains("flatpickr-input")) {
      return original;
    }
  }
  return input;
}
